import classNames from 'classnames';
import { mapValues } from 'lodash';
import React, { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, FormGroup, Label, Nav, NavItem, NavLink } from 'reactstrap';
import { gql, useMutation } from 'urql';
import { ErrorLayout } from '.';
import { useId } from '../hooks';
import { HacpackMutation, HacpackMutationChangeHandlingPositionArguments, Handling, Terminal } from '../types/graph';

type TerminalHandlingPositionProps = {
    handling: Handling,
    terminal: Terminal
};

enum TerminalHandlingPositionMode {
    CHECK_IN,
    CHANGE_POSITION,
    CHECK_OUT
}

const modeNameKeys: Record<TerminalHandlingPositionMode, string> = {
    [TerminalHandlingPositionMode.CHECK_IN]: 'Ingresso',
    [TerminalHandlingPositionMode.CHANGE_POSITION]: 'Cambio posizione',
    [TerminalHandlingPositionMode.CHECK_OUT]: 'Uscita',
}

const modeButtonKeys: Record<TerminalHandlingPositionMode, string> = {
    [TerminalHandlingPositionMode.CHECK_IN]: 'Conferma ingresso',
    [TerminalHandlingPositionMode.CHANGE_POSITION]: 'Sposta',
    [TerminalHandlingPositionMode.CHECK_OUT]: 'Conferma uscita',
}

type TerminalHandlingPositionFormValues = {
    area: string,
    bay?: string,
    row?: string,
    tier?: string
}

type HacpackMutationChangeHandlingPositionResult = Pick<HacpackMutation, 'changeHandlingPosition'>;

const changeHandlingPositionMutation = gql<HacpackMutationChangeHandlingPositionResult, HacpackMutationChangeHandlingPositionArguments>`
    mutation ($handlingId: Long!, $checkedIn: Boolean, $checkedOut: Boolean, $area: String, $bay: String, $row: String, $tier: String) {
        changeHandlingPosition(handlingId: $handlingId, checkedIn: $checkedIn, checkedOut: $checkedOut, area: $area, bay: $bay, row: $row, tier: $tier)  {
            isOk
        }
    }
`;

const TerminalHandlingPosition = ({ handling, terminal }: TerminalHandlingPositionProps) => {

    const [result, changeHandlingPosition] = useMutation<HacpackMutationChangeHandlingPositionResult, HacpackMutationChangeHandlingPositionArguments>(changeHandlingPositionMutation);
    const { error, fetching: fetchingApproval } = result;

    const terminalAreas = terminal.areas ?? [];

    const [selectedAreaName, setSelectedAreaName] = useState(handling.area);

    const { t } = useTranslation('common');

    const { id } = useId();
    const form = useForm<TerminalHandlingPositionFormValues>();

    //const { currentUser, fetchingCurrentUser } = useCurrentUser();

    const { handleSubmit, register } = form;
    //const { errors } = formState;

    const positionDisabled = selectedAreaName === "";

    const [modeTitles, modeButtons] = useMemo(() => [modeNameKeys, modeButtonKeys].map((m) => mapValues(m, (v) => t(v))), [t]);

    const availableModes = useMemo(() => handling.checkedIn === true
        ? [TerminalHandlingPositionMode.CHANGE_POSITION, TerminalHandlingPositionMode.CHECK_OUT]
        : handling.checkedOut === true ? null : [TerminalHandlingPositionMode.CHECK_IN],
        [handling]);

    const [activeMode, setActiveMode] = useState(availableModes?.[0] ?? null);

    const onSubmit = useCallback(({ area, bay, row, tier }: TerminalHandlingPositionFormValues) => {
        if (handling.id === undefined) {
            return;
        }

        const checkedIn = activeMode === TerminalHandlingPositionMode.CHECK_IN ? true : null;
        const checkedOut = activeMode === TerminalHandlingPositionMode.CHECK_OUT ? true : null;

        const args: HacpackMutationChangeHandlingPositionArguments = {
            handlingId: handling?.id,
            checkedIn,
            checkedOut,
            area: checkedOut ? "" : area,
            bay: checkedOut ? "" : bay ?? "",
            row: checkedOut ? "" : row ?? "",
            tier: checkedOut ? "" : tier ?? "",
        };

        changeHandlingPosition(args).then(({ data }) => {
            console.log(data);
            if (data?.changeHandlingPosition?.isOk !== true) {
                return;
            }
            window.location.reload();
        });
    }, [handling, activeMode, changeHandlingPosition]);

    const areaInputFieldId = `area${id}`;
    const { onChange: onAreaChange, ...areaInputField } = register('area', { required: true });

    const bayInputFieldId = `bay${id}`;

    const rowInputFieldId = `row${id}`;

    const tierInputFieldId = `tier${id}`;

    const handleAreaChange = useCallback((e: ChangeEvent<HTMLSelectElement>) => {
        setSelectedAreaName(e.target.value);
        onAreaChange(e);
    }, [onAreaChange, setSelectedAreaName]);

    const positionFields = activeMode !== TerminalHandlingPositionMode.CHECK_OUT
        ? <>
            <h6 className="card-title">{t('Posizione')}</h6>
            <div className="form form-inline">
                <FormGroup className="mb-2 mr-2 mr-sm-3">
                    <Label htmlFor={areaInputFieldId} className="mr-sm-2">{t('Area')}</Label>
                    <select id={areaInputFieldId} className='form-control' defaultValue={handling.area ?? ""} required onChange={handleAreaChange} {...areaInputField}>
                        <option value="">{t('--seleziona l\'area--')}</option>
                        {terminalAreas.map(({ id, name, description }) => <option key={id} value={name}>{description??name}</option>)}
                    </select>
                </FormGroup>

                <FormGroup className="mb-2 mr-2 mr-sm-3">
                    <Label htmlFor={bayInputFieldId} className="mr-sm-2">{t('Blocco')}</Label>
                    <input id={bayInputFieldId} className='form-control' defaultValue={handling.bay ?? ""} type="text" disabled={positionDisabled} placeholder="001,002,003..." {...register('bay', { pattern: /^[0-9]{3}$/ })} />
                </FormGroup>

                <FormGroup className="mb-2 mr-2 mr-sm-3">
                    <Label htmlFor={rowInputFieldId} className="mr-sm-2">{t('Fila')}</Label>
                    <input id={rowInputFieldId} className='form-control' defaultValue={handling.row ?? ""} type="text" disabled={positionDisabled} placeholder="001,002,003..." {...register('row', { pattern: /^[0-9]{3}$/ })} />
                </FormGroup>

                <FormGroup className="mb-2 mr-2 mr-sm-3">
                    <Label htmlFor={tierInputFieldId} className="mr-sm-2">{t('Tiro')}</Label>
                    <input id={tierInputFieldId} className='form-control' defaultValue={handling.tier ?? ""} type="number" disabled={positionDisabled} min="80" max="90" step="2" placeholder="80,82..." {...register('tier', { pattern: /^8(0|2|4|6|8)|90$/ })} />
                </FormGroup>
            </div>
        </>
        : null;

    if (activeMode === null) {
        return null;
    }

    return <form id={`handlingPositionForm-${id}`} className="m-3" onSubmit={handleSubmit(onSubmit)} noValidate>
        <h3>{t('Movimentazione')}</h3>
        {(error !== undefined) ? <ErrorLayout>{error.message}</ErrorLayout> : null}
        <div className="card">
            <div className="card-header">
                <Nav tabs card>
                    {availableModes?.map((mode) =>
                        <NavItem>
                            <NavLink className={classNames({ active: mode === activeMode })} onClick={() => { setActiveMode(mode) }}>{
                                modeTitles[mode]
                            }</NavLink>
                        </NavItem>
                    )}
                </Nav>
            </div>
            <div className="card-body">
                {positionFields}
                <Button disabled={fetchingApproval} type="submit">{modeButtons[activeMode]}</Button>
            </div>
        </div>
    </form>
};

export default TerminalHandlingPosition;